import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listAdminBooks, publishBook, unpublishBook, archiveBook, restoreBook, exportBooks } from "@/lib/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Plus, Download, Search, ExternalLink, Eye, EyeOff, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "draft", label: "Drafts" },
  { id: "published", label: "Published" },
  { id: "archived", label: "Archived" },
];

export default function AdminBooksList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [books, setBooks] = useState([]);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");

  const load = () => listAdminBooks().then(setBooks).catch(() => toast.error("Failed to load books"));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = books.slice();
    if (tab !== "all") list = list.filter(b => (b.publish_status || "published") === tab);
    if (q.trim()) list = list.filter(b => b.title.toLowerCase().includes(q.toLowerCase()));
    return list;
  }, [books, tab, q]);

  const doExport = async () => {
    try {
      const data = await exportBooks();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
      a.download = `books-export-${new Date().toISOString().slice(0, 10)}.json`; a.click();
    } catch { toast.error("Export failed (owner only)"); }
  };

  const act = async (fn, id, label) => {
    try { await fn(id); toast.success(label); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const isOwner = user?.role === "owner";
  const canEdit = user?.role === "owner" || user?.role === "editor";

  return (
    <div data-testid="admin-books-page">
      <PageHeader eyebrow="Content" title="Books" note="Manage principal books and their language editions." />
      <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-2" data-testid="books-tabs">
          {STATUS_TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className="px-4 py-2 text-xs font-medium border transition-colors"
              style={{ borderColor: tab === t.id ? "var(--gi-maroon)" : "var(--gi-border)", background: tab === t.id ? "var(--gi-maroon)" : "#fff", color: tab === t.id ? "#fff" : "var(--gi-charcoal-2)", letterSpacing: "0.06em", textTransform: "uppercase" }}
              data-testid={`books-tab-${t.id}`}>{t.label}</button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 border px-3 bg-white" style={{ borderColor: "var(--gi-border)" }}>
            <Search size={14} style={{ color: "var(--gi-muted)" }} />
            <input placeholder="Search titles" className="py-2 text-sm outline-none" value={q} onChange={e => setQ(e.target.value)} data-testid="books-search-input" />
          </div>
          {isOwner && <button onClick={doExport} className="btn-ghost" data-testid="books-export-btn"><Download size={14} /> Export JSON</button>}
          {canEdit && <Link to="/admin/books/new" className="btn-maroon" data-testid="books-new-btn"><Plus size={14} /> New Book</Link>}
        </div>
      </div>

      <div className="mt-6 border bg-white overflow-x-auto" style={{ borderColor: "var(--gi-border)" }} data-testid="books-table">
        <div className="grid grid-cols-[minmax(220px,1fr)_120px_120px_140px_180px] gap-4 px-5 py-3 border-b text-[10px] font-accent min-w-[780px]" style={{ borderColor: "var(--gi-border)", color: "var(--gi-muted)", letterSpacing: "0.14em" }}>
          <div>Title</div><div>Series</div><div>Editions</div><div>Workflow</div><div className="text-right">Actions</div>
        </div>
        {filtered.length === 0 && <div className="p-8 text-sm text-center" style={{ color: "var(--gi-muted)" }} data-testid="books-empty">No books in this view.</div>}
        {filtered.map(b => (
          <div key={b.id} className="grid grid-cols-[1fr_120px_120px_140px_180px] gap-4 px-5 py-4 border-b last:border-b-0 items-center text-sm" style={{ borderColor: "var(--gi-divider)" }} data-testid={`books-row-${b.slug}`}>
            <div>
              <div className="font-heading text-base" style={{ color: "var(--gi-charcoal)" }}>{b.title}</div>
              <div className="text-xs" style={{ color: "var(--gi-muted)" }}>{b.slug}</div>
            </div>
            <div className="text-xs" style={{ color: "var(--gi-muted)" }}>{b.series?.replace(" Series", "")}</div>
            <div className="text-xs" style={{ color: "var(--gi-muted)" }}>{(b.editions || []).join(", ")}</div>
            <div>
              <StatusBadge status={b.publish_status || "published"} />
              <div className="text-[10px] mt-1" style={{ color: "var(--gi-muted)" }}>Editorial: {b.status}</div>
            </div>
            <div className="flex justify-end gap-2">
              <Link to={`/admin/books/${b.id}`} className="btn-ghost" data-testid={`books-edit-${b.slug}`}>Edit</Link>
              {isOwner && (b.publish_status || "published") !== "published" && (b.publish_status || "published") !== "archived" && (
                <button onClick={() => act(publishBook, b.id, "Published")} className="btn-ghost" data-testid={`books-publish-${b.slug}`} title="Publish"><Eye size={12} /></button>
              )}
              {isOwner && b.publish_status === "published" && (
                <button onClick={() => act(unpublishBook, b.id, "Unpublished")} className="btn-ghost" data-testid={`books-unpublish-${b.slug}`} title="Unpublish"><EyeOff size={12} /></button>
              )}
              {canEdit && b.publish_status !== "archived" && (
                <button onClick={() => window.confirm("Archive this book? It will be moved to trash and can be restored.") && act(archiveBook, b.id, "Archived")} className="btn-ghost" title="Archive" data-testid={`books-archive-${b.slug}`}><Archive size={12} /></button>
              )}
              {canEdit && b.publish_status === "archived" && (
                <button onClick={() => act(restoreBook, b.id, "Restored")} className="btn-ghost" title="Restore" data-testid={`books-restore-${b.slug}`}><RotateCcw size={12} /></button>
              )}
              <a href={`/books/${b.slug}`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid={`books-view-${b.slug}`} title="Open public page"><ExternalLink size={12} /></a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const StatusBadge = ({ status }) => {
  const map = {
    published: { bg: "#E6F1E7", fg: "#2E5D34", label: "Published" },
    draft: { bg: "var(--gi-ivory-2)", fg: "var(--gi-maroon)", label: "Draft" },
    archived: { bg: "#F1E4E4", fg: "#7A2E2E", label: "Archived" },
  };
  const s = map[status] || map.published;
  return <span className="inline-block px-2 py-0.5 text-[10px] font-medium" style={{ background: s.bg, color: s.fg, letterSpacing: "0.1em", textTransform: "uppercase" }} data-testid={`status-${status}`}>{s.label}</span>;
};
